// ============================================================================
// ROLE PERMISSION MASTER SCREEN — CRS
// ============================================================================
// Admin CRUD screen for admin.tbl_role_permissions via crs-api
// /api/v1/RolePermissions endpoint.
//
// Configures which modules and actions each Keycloak role can access.
// Changes take effect for users on their next login (permissions are fetched
// and stored in localStorage during the authentication flow — see AuthContext).
//
// Bound to the left sidebar via:
//  - components/common/ComponentRegistry.tsx → COMPONENT_REGISTRY['RolePermissionsScreen']
//  - login-interface.tsx → navigationMenu + modulesConfig
//  - Layout/main_layout.tsx → ENTERPRISE_LAYOUT_CONFIG.navigation + .modules['role-permissions']
//
// Mirrors the prima-web RolePermissionScreen 1:1 with CRS adjustments
// (rowVersion in delete, .module-content marginTop, CRS module slugs).

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import {
  useRolePermissions,
  useCreateRolePermission,
  useUpdateRolePermission,
  useDeleteRolePermission,
} from 'hooks/api/useRolePermissions';
import type {
  RolePermission,
  RolePermissionSearchRequest,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
} from 'api/types';
import {
  RolePermissionFormModal,
  RolePermissionViewModal,
  type RolePermissionFormData,
  type RolePermissionRecord,
} from 'components/modals/modules/iam-module/RolePermissionModal';
import type { AccessOption, RoleAccessFormState } from 'components/modals/modules/iam-module/RoleOutletAccessSection';
import { dineOpsService } from 'api/services/dineOpsService';
import type { RoleOutletUser } from 'api/types';

// ============================================================================
// Constants
// ============================================================================

const INITIAL_FORM_DATA: RolePermissionFormData = {
  roleCode: '',
  roleName: '',
  description: '',
  visibleModules: [],
  moduleActions: {},
  // Default new custom roles below every baseline (system=0, admin=10,
  // booking_agent=40, user=100). Admins can adjust via the Priority input.
  priority: 1000,
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active only' },
  { value: 'false', label: 'Inactive only' },
];

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'system', label: 'System roles' },
  { value: 'custom', label: 'Custom roles' },
];

/** Format ISO date to a short display string (DD MMM YYYY). */
const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

/** Convert API RolePermission to the modal's RolePermissionRecord shape. */
const toRecord = (rp: RolePermission): RolePermissionRecord => ({
  id: rp.id,
  roleCode: rp.roleCode,
  roleName: rp.roleName,
  description: rp.description ?? undefined,
  visibleModules: Array.isArray(rp.visibleModules) ? rp.visibleModules : [],
  moduleActions: rp.moduleActions ?? {},
  isSystemRole: rp.isSystemRole,
  isActive: rp.isActive,
  // Default to 1000 if the backend somehow omits it (older payloads pre-020).
  priority: typeof rp.priority === 'number' ? rp.priority : 1000,
  createdAt: rp.createdAt ?? '',
  updatedAt: rp.updatedAt ?? undefined,
  rowVersion: rp.rowVersion,
});

// ============================================================================
// Props
// ============================================================================

interface RolePermissionsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view'} the current user is allowed to
   * perform on this module. Set by main_layout.tsx from
   * MODULES_CONFIG[moduleId].actions, which is in turn populated at login by
   * applyRoleActions() against the role's module_actions JSONB.
   *
   * Defaults to [] — fail-closed. If main_layout misses passing the prop
   * (e.g. in a test or direct route mount), the screen shows view-only.
   */
  allowedActions?: string[];
}

// ============================================================================
// Screen component
// ============================================================================

const RolePermissionsScreen: React.FC<RolePermissionsScreenProps> = ({
  moduleIcon,
  tabId,
  allowedActions = [],
}) => {
  // --------------------------------------------------------------------------
  // Server-side query state
  // --------------------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<RolePermissionSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, pagination, loading, refetch } = useRolePermissions(queryParams);

  // --------------------------------------------------------------------------
  // UI state
  // --------------------------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // --------------------------------------------------------------------------
  // Search debounce (400ms)
  // --------------------------------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Pagination handlers
  // --------------------------------------------------------------------------
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // --------------------------------------------------------------------------
  // Form modal state (parent owns formData)
  // --------------------------------------------------------------------------
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<RolePermission | null>(null);
  const [formData, setFormData] = useState<RolePermissionFormData>(INITIAL_FORM_DATA);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Snapshot of `formData` at the moment the modal opens — used to compute
   * `isDirty`. Captured by handleAddNew / handleEdit (so we don't depend on
   * an effect inside the stateless modal component). Empty string before any
   * modal has been opened.
   */
  const initialSnapshotRef = useRef<string>('');

  /** TRUE when formData differs from the snapshot taken at modal-open time. */
  const isDirty = useMemo(
    () => initialSnapshotRef.current !== JSON.stringify(formData),
    [formData],
  );

  // --------------------------------------------------------------------------
  // Restaurant tab → Outlet access (dine.tbl_role_outlet_access) and Kitchen
  // departments (dine.tbl_role_station_access). Loaded when the modal opens;
  // each saved after the role, and only when it changed.
  // --------------------------------------------------------------------------
  const [outletOptions, setOutletOptions] = useState<AccessOption[]>([]);
  const [stationOptions, setStationOptions] = useState<AccessOption[]>([]);
  const [outletUsers, setOutletUsers] = useState<RoleOutletUser[]>([]);
  const [outletAccessLoading, setOutletAccessLoading] = useState(false);
  const [outletAccessError, setOutletAccessError] = useState<string | undefined>();
  /** The blocks as loaded: what "changed" is measured against when saving. */
  const accessBaselineRef = useRef<{ outlet: RoleAccessFormState | null; station: RoleAccessFormState | null }>({ outlet: null, station: null });
  /** Ignores a load that finishes after the modal was reopened for another role. */
  const outletLoadSeqRef = useRef(0);

  const loadOutletAccess = useCallback(async (roleCode: string | null) => {
    const seq = ++outletLoadSeqRef.current;
    accessBaselineRef.current = { outlet: null, station: null };
    setOutletAccessError(undefined);
    setOutletAccessLoading(true);
    try {
      const [outlets, stations, users, access, stationAccess] = await Promise.all([
        dineOpsService.getOutlets(true),
        dineOpsService.getStations({ page: 1, pageSize: 200 }),
        dineOpsService.getRoleOutletUsers(),
        roleCode ? dineOpsService.getRoleOutletAccess(roleCode) : Promise.resolve(null),
        roleCode ? dineOpsService.getRoleStationAccess(roleCode) : Promise.resolve(null),
      ]);
      if (seq !== outletLoadSeqRef.current) return;
      const byUser = (pick: (u: RoleOutletUser) => string[] | undefined) => {
        const map: Record<string, string[]> = {};
        for (const u of users) map[u.userId] = Array.from(new Set(pick(u) ?? [])).sort();
        return map;
      };
      const outletLoaded: RoleAccessFormState = {
        accessMode: access?.accessMode ?? 'PER_USER',
        ids: Array.from(new Set(access?.outletIds ?? [])).sort(),
        userIds: byUser((u) => u.outletIds),
      };
      // A role with nothing saved works every department, as before the setting existed.
      const stationLoaded: RoleAccessFormState = {
        accessMode: stationAccess?.accessMode ?? 'ALL',
        ids: Array.from(new Set(stationAccess?.stationIds ?? [])).sort(),
        userIds: byUser((u) => u.stationIds),
      };
      setOutletOptions(outlets.map((o) => ({ id: o.id, code: o.outletCode, name: o.outletName, isActive: o.isActive })));
      setStationOptions((stations.data ?? []).map((s) => ({ id: s.id, code: s.stationCode, name: s.stationName, isActive: s.isActive !== false })));
      setOutletUsers(users);
      accessBaselineRef.current = { outlet: outletLoaded, station: stationLoaded };
      // The loaded values are part of the opening state, not a change: the snapshot takes them too.
      const snapshot = JSON.parse(initialSnapshotRef.current || '{}');
      snapshot.outletAccess = outletLoaded;
      snapshot.stationAccess = stationLoaded;
      initialSnapshotRef.current = JSON.stringify(snapshot);
      setFormData((prev) => ({ ...prev, outletAccess: outletLoaded, stationAccess: stationLoaded }));
    } catch (err: any) {
      if (seq !== outletLoadSeqRef.current) return;
      setOutletAccessError(err?.response?.data?.message || 'Outlet access could not be loaded. Close and reopen the role to try again.');
    } finally {
      if (seq === outletLoadSeqRef.current) setOutletAccessLoading(false);
    }
  }, []);

  /**
   * Saves outlet access and kitchen departments, each when it changed (or was never saved for a
   * new role). Per user, only the users whose ticks changed are sent. Returns an error message, or null.
   */
  const saveOutletAccess = useCallback(async (roleCode: string, data: RolePermissionFormData, isNew: boolean): Promise<string | null> => {
    const changedUsers = (current: RoleAccessFormState, baseline: RoleAccessFormState) => (current.accessMode === 'PER_USER'
      ? Object.entries(current.userIds).filter(([userId, ids]) => JSON.stringify(ids) !== JSON.stringify(baseline.userIds[userId] ?? []))
      : []);
    const errors: string[] = [];
    // A new role's untouched defaults are saved only when it has Restaurant screens: a role
    // without them needs no outlet rows, and the call fails for an admin without DineOps rights.
    const saveDefaults = isNew && data.visibleModules.some((m) => m.startsWith('dineops-'));
    const { outlet: outletBase, station: stationBase } = accessBaselineRef.current;
    const outlet = data.outletAccess;
    if (outlet && outletBase && (saveDefaults || JSON.stringify(outlet) !== JSON.stringify(outletBase))) {
      try {
        await dineOpsService.saveRoleOutletAccess(roleCode, {
          accessMode: outlet.accessMode,
          outletIds: outlet.accessMode === 'SELECTED' ? outlet.ids : [],
          users: changedUsers(outlet, outletBase).map(([userId, outletIds]) => ({ userId, outletIds })),
        });
      } catch (err: any) { errors.push(`outlet access: ${err?.response?.data?.message || err?.message || 'not saved'}`); }
    }
    const station = data.stationAccess;
    if (station && stationBase && (saveDefaults || JSON.stringify(station) !== JSON.stringify(stationBase))) {
      try {
        await dineOpsService.saveRoleStationAccess(roleCode, {
          accessMode: station.accessMode,
          stationIds: station.accessMode === 'SELECTED' ? station.ids : [],
          users: changedUsers(station, stationBase).map(([userId, stationIds]) => ({ userId, stationIds })),
        });
      } catch (err: any) { errors.push(`kitchen station access:${err?.response?.data?.message || err?.message || 'not saved'}`); }
    }
    return errors.length ? errors.join('; ') : null;
  }, []);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  // --------------------------------------------------------------------------
  // View modal state
  // --------------------------------------------------------------------------
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<RolePermissionRecord | null>(null);

  // --------------------------------------------------------------------------
  // Delete state
  // --------------------------------------------------------------------------
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RolePermission | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // --------------------------------------------------------------------------
  // Mutation hooks
  // --------------------------------------------------------------------------
  const { createRolePermission } = useCreateRolePermission();
  const { updateRolePermission } = useUpdateRolePermission();
  const { deleteRolePermission } = useDeleteRolePermission();

  // --------------------------------------------------------------------------
  // Grid columns
  // --------------------------------------------------------------------------
  const tableColumns: SyutGridColumn<RolePermission>[] = useMemo(
    () => [
      { id: 'roleCode', header: 'Role Code', accessorKey: 'roleCode', size: 180, enableSorting: true },
      { id: 'roleName', header: 'Role Name', accessorKey: 'roleName', size: 200, enableSorting: true },
      {
        id: 'description',
        header: 'Description',
        accessorKey: 'description',
        enableSorting: false,
        cell: ({ row }) => {
          const desc = row.original.description;
          if (!desc) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
          return (
            <span title={desc} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>
              {desc}
            </span>
          );
        },
      },
      {
        id: 'modules',
        header: 'Modules',
        accessorKey: 'visibleModules',
        size: 90,
        enableSorting: false,
        cell: ({ row }) => {
          const count = Array.isArray(row.original.visibleModules) ? row.original.visibleModules.length : 0;
          return <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{count}</span>;
        },
      },
      {
        id: 'isSystemRole',
        header: 'Type',
        accessorKey: 'isSystemRole',
        size: 110,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.isSystemRole ? (
            <span className="badge" style={{ background: 'var(--primary-light, rgba(59,130,246,0.12))', color: 'var(--primary)', fontSize: '0.72rem' }}>
              System
            </span>
          ) : (
            <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
              Custom
            </span>
          ),
      },
      {
        id: 'isActive',
        header: 'Status',
        accessorKey: 'isActive',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <span className={`badge badge-${row.original.isActive ? 'active' : 'inactive'}`}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
    ],
    [],
  );

  // --------------------------------------------------------------------------
  // Handlers — open modals
  // --------------------------------------------------------------------------
  const handleAddNew = useCallback(() => {
    setSelectedRecord(null);
    setFormData(INITIAL_FORM_DATA);
    initialSnapshotRef.current = JSON.stringify(INITIAL_FORM_DATA);
    setFormMode('create');
    setFormModalOpen(true);
    void loadOutletAccess(null);
  }, [loadOutletAccess]);

  const handleEdit = useCallback((record: RolePermission) => {
    setSelectedRecord(record);
    const populated: RolePermissionFormData = {
      roleCode: record.roleCode,
      roleName: record.roleName,
      description: record.description ?? '',
      visibleModules: Array.isArray(record.visibleModules) ? record.visibleModules : [],
      moduleActions: record.moduleActions ?? {},
      priority: typeof record.priority === 'number' ? record.priority : 1000,
    };
    setFormData(populated);
    initialSnapshotRef.current = JSON.stringify(populated);
    setFormMode('edit');
    setFormModalOpen(true);
    void loadOutletAccess(record.roleCode);
  }, [loadOutletAccess]);

  const handleView = useCallback((record: RolePermission) => {
    setViewRecord(toRecord(record));
    setViewModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((record: RolePermission) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // Save handler — step 1: open confirmation dialog
  // --------------------------------------------------------------------------
  const handleSave = useCallback(() => {
    setSaveConfirmOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // Save handler — step 2: confirmed, perform API call
  // --------------------------------------------------------------------------
  const handleConfirmSave = useCallback(async () => {
    setIsSaving(true);
    try {
      if (formMode === 'create') {
        const payload: CreateRolePermissionRequest = {
          roleCode: formData.roleCode.trim(),
          roleName: formData.roleName.trim(),
          description: formData.description.trim() || undefined,
          visibleModules: formData.visibleModules,
          moduleActions: formData.moduleActions,
          priority: formData.priority,
        };
        await createRolePermission(payload);
        const outletError = await saveOutletAccess(payload.roleCode, formData, true);
        if (outletError) notificationService.toast.error(`Role "${payload.roleCode}" created, but its ${outletError}`, 8000);
        else notificationService.toast.success(`Role permission "${payload.roleCode}" created.`);
      } else if (selectedRecord) {
        const payload: UpdateRolePermissionRequest = {
          roleCode: selectedRecord.roleCode, // role code is read-only on edit; sent unchanged
          roleName: formData.roleName.trim(),
          description: formData.description.trim() || undefined,
          visibleModules: formData.visibleModules,
          moduleActions: formData.moduleActions,
          priority: formData.priority,
          rowVersion: selectedRecord.rowVersion,
        };
        await updateRolePermission(selectedRecord.id, payload);
        const outletError = await saveOutletAccess(selectedRecord.roleCode, formData, false);
        if (outletError) notificationService.toast.error(`Role "${selectedRecord.roleCode}" saved, but not its ${outletError}`, 8000);
        else notificationService.toast.success(`Role permission "${selectedRecord.roleCode}" updated.`);
      }
      setIsSaving(false);
      setSaveConfirmOpen(false);
      setFormModalOpen(false);
      setSelectedRecord(null);
      setFormData(INITIAL_FORM_DATA);
      refetch();
    } catch (err: any) {
      setIsSaving(false);
      setSaveConfirmOpen(false);
      const apiBody = err?.response?.data;
      let message = 'Failed to save role permission. Please try again.';
      if (typeof apiBody?.message === 'string' && apiBody.message) {
        message = apiBody.message;
      } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
        message = apiBody.errors.join(', ');
      } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
        message = Object.values(apiBody.errors).flat().join(', ');
      } else if (err?.message) {
        message = err.message;
      }
      notificationService.toast.error(message, 6000);
    }
  }, [formMode, formData, selectedRecord, createRolePermission, updateRolePermission, refetch, saveOutletAccess]);

  // --------------------------------------------------------------------------
  // Delete handler
  // --------------------------------------------------------------------------
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteRolePermission(deleteTarget.id, deleteTarget.rowVersion);
      notificationService.toast.success(`Role permission "${deleteTarget.roleCode}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete role permission.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteRolePermission, refetch]);

  // --------------------------------------------------------------------------
  // Active filter count
  // --------------------------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.includeInactive === false) count++;
    if (queryParams.isSystemRole !== undefined) count++;
    return count;
  }, [queryParams.includeInactive, queryParams.isSystemRole]);

  // --------------------------------------------------------------------------
  // Filter Apply / Clear
  // --------------------------------------------------------------------------
  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      // Status filter: 'true' (active only) → includeInactive=false; 'false' (inactive only) is not supported
      // server-side because the API uses includeInactive flag — for now treat 'false' as activeOnly off.
      includeInactive: statusFilter === 'true' ? false : true,
      isSystemRole: typeFilter === 'system' ? true : typeFilter === 'custom' ? false : undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [statusFilter, typeFilter]);

  const clearFilters = useCallback(() => {
    setStatusFilter('');
    setTypeFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Row actions
  // --------------------------------------------------------------------------
  const getRowActions = useCallback(
    (row: RolePermission): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [
        {
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        },
      ];
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
          disabled: row.isSystemRole,
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick],
  );

  // --------------------------------------------------------------------------
  // ModuleHeader action buttons
  // --------------------------------------------------------------------------
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add')
        ? [{
            id: 'add',
            icon: <IconSystem.add size={18} />,
            label: 'Add',
            tooltip: 'Add New Role Permission',
            onClick: handleAddNew,
            variant: 'icon' as const,
          }]
        : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch],
  );

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Role Permissions"
        description="Configure module visibility and action permissions for each Keycloak role."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : React.createElement(IconSystem.lock, { size: 18 })
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search role code, name…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          appliedCount: activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              enableSorting
              rowActions={
                allowedActions.includes('view') ||
                allowedActions.includes('edit') ||
                allowedActions.includes('delete')
                  ? getRowActions
                  : undefined
              }
              emptyMessage={loading ? 'Loading role permissions…' : 'No role permissions found'}
            />
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button
                className="search-panel-close"
                onClick={() => setFilterOpen(false)}
                title="Close filters"
              >
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Role Type"
                  options={TYPE_FILTER_OPTIONS}
                  value={typeFilter}
                  onChange={(val) => setTypeFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Form modal — Create / Edit */}
      <RolePermissionFormModal
        isOpen={formModalOpen}
        mode={formMode}
        formData={formData}
        isSystemRole={selectedRecord?.isSystemRole}
        isDirty={isDirty}
        onFormDataChange={setFormData}
        onSave={handleSave}
        outletOptions={outletOptions}
        stationOptions={stationOptions}
        outletUsers={outletUsers}
        outletAccessLoading={outletAccessLoading}
        outletAccessError={outletAccessError}
        onClose={() => {
          if (!isSaving) {
            setFormModalOpen(false);
            setSelectedRecord(null);
            setFormData(INITIAL_FORM_DATA);
          }
        }}
      />

      {/* View modal */}
      <RolePermissionViewModal
        isOpen={viewModalOpen}
        record={viewRecord}
        onClose={() => {
          setViewModalOpen(false);
          setViewRecord(null);
        }}
      />

      {/* Save confirmation — create / edit */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={formMode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={
          formMode === 'create'
            ? `Are you sure you want to create the role permission for "${formData.roleCode}"?`
            : `Are you sure you want to save changes to "${selectedRecord?.roleCode}"? This will affect all users with this role on their next login.`
        }
        confirmLabel={formMode === 'create' ? 'Create' : 'Save Changes'}
        cancelLabel="Cancel"
        loading={isSaving}
        loadingMessage="Saving…"
        onConfirm={handleConfirmSave}
        onCancel={() => { if (!isSaving) setSaveConfirmOpen(false); }}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Role Permission"
        message={
          deleteTarget
            ? `Are you sure you want to delete the role permission for "${deleteTarget.roleCode} — ${deleteTarget.roleName}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
};

export default RolePermissionsScreen;
